import type { GetTimesheetProposalApiResponse } from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';

export function getLastAcceptedHourIds(
  proposal: GetTimesheetProposalApiResponse['proposal']
): number[] {
  const acceptedLog = [...(proposal.log || [])]
    .reverse()
    .find(log => log.action === 'status_changed' && log.details?.to_status === 'accepted');

  return Array.isArray(acceptedLog?.details?.saved_hours)
    ? (acceptedLog.details.saved_hours as number[])
    : [];
}

// Fields whose change does NOT affect the AI-generated note text. An
// entry_updated log that only touches these is logged for audit but should
// not flip the note into a "stale, regenerate me" state.
const NON_CONTENT_FIELDS = new Set(['timely_label_ids', 'resource_id']);

// Extract entry IDs that were edited by a user, based on proposal logs.
// This avoids relying on updated_at which may change due to system status updates (e.g. submit/revert).
// Excludes entries that were regenerated and haven't been edited since regeneration.
export const getEditedEntryIdsFromLogs = (
  logs: NonNullable<GetTimesheetProposalApiResponse['proposal']['log']> | undefined,
  entries?: NonNullable<GetTimesheetProposalApiResponse['proposal']['entries']>
): Set<string> => {
  const edited = new Set<string>();
  if (!logs || logs.length === 0) return edited;

  const consideredActions = new Set<
    NonNullable<GetTimesheetProposalApiResponse['proposal']['log']>[number]['action']
  >([
    'entry_updated',
    'entry_added',
    'entry_merged',
    'item_updated',
    'item_assigned',
    'item_unassigned',
  ]);

  // Build map of entry_id -> last edit timestamp
  const lastEditByEntry = new Map<string, string>();

  for (const log of logs) {
    if (!consideredActions.has(log.action)) continue;
    // Only count explicit user edits
    if (!log.user_id) continue;

    const details = log.details || {};

    // entry_updated logs carry the changed field names; skip when the change
    // is purely categorisation (labels, linked work) — those don't make the
    // generated note stale.
    if (log.action === 'entry_updated') {
      const updatedFields = Array.isArray(details.updated_fields)
        ? (details.updated_fields as string[])
        : [];
      if (updatedFields.length > 0 && updatedFields.every(f => NON_CONTENT_FIELDS.has(f))) {
        continue;
      }
    }

    const maybeIds: Array<unknown> = [details.entry_id, details.moved_to_entry_id];

    for (const id of maybeIds) {
      if (typeof id === 'string' && id) {
        edited.add(id);
        // Track the most recent edit for this entry
        const currentLast = lastEditByEntry.get(id);
        if (!currentLast || log.timestamp > currentLast) {
          lastEditByEntry.set(id, log.timestamp);
        }
      }
    }
  }

  // Filter out entries that were regenerated and not edited afterwards
  if (entries) {
    for (const entry of entries) {
      if (!edited.has(entry.id)) continue;

      const metadata = entry.metadata as Record<string, unknown> | null | undefined;
      const noteLastRegeneratedAt = metadata?.['note_last_regenerated_at'];

      // If entry has regeneration timestamp and last edit was before it, exclude it
      if (typeof noteLastRegeneratedAt === 'string') {
        const lastEdit = lastEditByEntry.get(entry.id);
        if (!lastEdit || lastEdit < noteLastRegeneratedAt) {
          edited.delete(entry.id);
        }
      }
    }
  }

  return edited;
};
